import { computed, onBeforeMount, ref } from 'vue';
import type { SerializedUser } from '@/api/users';
import { useUser } from './useUser';
import { usePermissions } from './usePermissions';

export interface NavSection {
  key: string;
  title: string;
  href: string;
  active: boolean;
}

// charge_closures precedes charges: detection is a substring match, first wins.
const SECTION_PATHS = [
  'caregivers', 'patients', 'payments', 'charge_closures', 'charges', 'today_shifts',
  'planning', 'route_plans', 'monthly_tracking', 'coverage_alerts', 'settings', 'dashboard',
  'inventory', 'erp_integration',
];

function detectSection(pathname: string): string {
  const match = SECTION_PATHS.find((section) => pathname.includes(section));

  return match || 'shifts';
}

// The integration page needs a company connected to its ERP and its own permission.
function erpIntegrationVisible(
  user: SerializedUser | undefined, hasPermission: (resource: string, action: string) => boolean,
): boolean {
  return !!user?.companyErpManagedCaregivers && hasPermission('erp_integration', 'manage');
}

// Permission-gated main navigation sections, shared by the app bar links and
// the mobile navigation drawer so both always offer the same destinations.
export function useNavSections() {
  const { user } = useUser();
  const { hasPermission } = usePermissions();

  const currentSection = ref('');

  onBeforeMount(() => {
    currentSection.value = detectSection(window.location.pathname);
  });

  const planningSectionActive = computed(() => (
    ['planning', 'route_plans', 'monthly_tracking', 'coverage_alerts'].includes(currentSection.value)
  ));

  function canSeeCarePlanning(): boolean {
    if (!user?.value?.companyCarePlanningEnabled) return false;
    return hasPermission.value('requirements', 'index') ||
      hasPermission.value('monthly_tracking', 'index');
  }

  function canSeeRoutePlans(): boolean {
    if (!user?.value?.companyRoutePlansEnabled) return false;
    return hasPermission.value('route_plans', 'index');
  }

  const canSeePlanning = computed(() => canSeeCarePlanning() || canSeeRoutePlans());

  const sections = computed<NavSection[]>(() => [
    {
      key: 'today_shifts',
      title: 'Hoy',
      href: '/today_shifts',
      show: hasPermission.value('shifts', 'index'),
      active: currentSection.value === 'today_shifts',
    },
    {
      key: 'shifts',
      title: 'Turnos',
      href: '/shifts',
      show: hasPermission.value('shifts', 'index'),
      active: currentSection.value === 'shifts',
    },
    {
      key: 'planning',
      title: 'Planificación',
      href: '/planning',
      show: canSeePlanning.value,
      active: planningSectionActive.value,
    },
    {
      key: 'caregivers',
      title: 'Profesionales',
      href: '/caregivers',
      show: hasPermission.value('caregivers', 'index'),
      active: currentSection.value === 'caregivers',
    },
    {
      key: 'patients',
      title: 'Pacientes',
      href: '/patients',
      show: hasPermission.value('patients', 'index'),
      active: currentSection.value === 'patients',
    },
    {
      key: 'inventory',
      title: 'Inventario',
      href: '/inventory',
      show: !!user?.value?.companyInventoryEnabled && hasPermission.value('inventory', 'index'),
      active: currentSection.value === 'inventory',
    },
    {
      key: 'charges',
      title: 'Cobros',
      href: '/charges',
      show: hasPermission.value('charges', 'index'),
      active: ['charges', 'charge_closures'].includes(currentSection.value),
    },
    {
      key: 'payments',
      title: 'Pagos',
      href: '/payments',
      show: hasPermission.value('payments', 'index'),
      active: currentSection.value === 'payments',
    },
    {
      key: 'erp_integration',
      title: 'Integración Odoo',
      href: '/erp_integration',
      show: erpIntegrationVisible(user?.value, hasPermission.value),
      active: currentSection.value === 'erp_integration',
    },
    {
      key: 'dashboard',
      title: 'Resumen',
      href: '/dashboard',
      show: hasPermission.value('dashboard', 'index'),
      active: currentSection.value === 'dashboard',
    },
    {
      key: 'settings',
      title: 'Configuración',
      href: '/settings',
      show: hasPermission.value('settings', 'index'),
      active: currentSection.value === 'settings',
    },
  ].filter((section) => section.show)
    .map(({ key, title, href, active }) => ({ key, title, href, active })));

  return { sections, currentSection };
}
